import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import React from "react";
import { css } from "styled-components";
import parse from "html-react-parser";
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import colorPallete, { bodyFonts, headlineFonts, themeFontWeights } from "@DSG/MobiusOverrides/styles/variables";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";

interface OwnProps {
    modalIsOpen: boolean;
    headlineText: React.ReactNode;
    messageText: string;
    cancelButtonText: string;
    submitButtonText: string;
    extendedStyles?: TwoButtonModalStyles;
    onCancel: (e: React.MouseEvent<HTMLElement, MouseEvent>) => void;
    onSubmit: (e: React.MouseEvent<HTMLElement, MouseEvent>) => void;
    onClose?: (e: React.MouseEvent<HTMLElement, MouseEvent>) => void;
    submitTestSelector?: string;
    dataTestSelector?: string;
}

type Props = OwnProps;

export interface TwoButtonModalStyles {
    modal?: ModalPresentationProps;
    container?: GridContainerProps;
    textGridItem?: GridItemProps;
    messageText?: TypographyProps;
    buttonsGridItem?: GridItemProps;
    submitButton?: ButtonPresentationProps;
    cancelButton?: ButtonPresentationProps;
}

export const twoButtonModalStyles: TwoButtonModalStyles = {
    modal: {
        size: 600,
        cssOverrides: {
            headlineTypography: css`
                font: ${headlineFonts.headline3SemiBoldSm.fontSize};
                line-height: ${headlineFonts.headline3SemiBoldSm.lineHeight};
                color: ${colorPallete.grey800};
                margin: 0;
                ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            font-size: ${headlineFonts.headline3SemiBoldLg.fontSize};
                            line-height: ${headlineFonts.headline3SemiBoldLg.lineHeight};
                        `
                    ],
                    "min",
                )}
            `
        }
    },
    messageText: {
        css: css`
            &[class*="TypographyStyle"] {
                font-size: ${bodyFonts.body2RegularLg.fontSize};
                line-height: ${bodyFonts.body2RegularLg.lineHeight};
                color: ${colorPallete.grey800};
                ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            font-size: ${bodyFonts.body1RegularLg.fontSize};
                            line-height: ${bodyFonts.body1RegularLg.lineHeight};
                        `
                    ],
                    "min",
                )}
            }
            .text-underline {
                text-decoration: underline;
            }
            strong {
                font-weight: ${themeFontWeights.semiBold};
            }
        `
    },
    textGridItem: {
        width: 12,
        css: css`
            padding-bottom: 16px;
            color: ${colorPallete.grey800};
            ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(
                theme,
                [
                    null,
                    null,
                    css`
                        padding-bottom: 24px;
                    `
                ],
                "min",
            )}
        `
    },
    buttonsGridItem: {
        width: 12,
        css: css`
            flex-direction: column;
            align-items: flex-end;
            row-gap: 8px;
            button {
                min-height: 38px;
                padding: 5px 24px;
                width: 100%;
            }
            ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(
                theme,
                [
                    null,
                    null,
                    css`
                        row-gap: 16px;
                        button {
                            min-height: 48px;
                            padding: 10px 24px;
                            max-width: 300px;
                        }
                    `
                ],
                "min",
            )}
        `,
    },
    cancelButton: {
        variant: "secondary",
    },
    submitButton: {
        variant: "primary",
    },
    container: {
        gap: 0,
    },
};

class TwoButtonModal extends React.Component<Props> {
    private readonly styles: TwoButtonModalStyles;

    constructor(props: Props) {
        super(props);

        this.styles = mergeToNew(twoButtonModalStyles, props.extendedStyles);
    }

    formCancelHandler = (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
        e.preventDefault();
        this.props.onCancel(e);
    };

    formSubmitHandler = (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
        e.preventDefault();
        this.props.onSubmit(e);
    };

    formCloseHandler = (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
        e.preventDefault();
        if (this.props.onClose) {
            this.props.onClose(e);
        } else {
            this.props.onCancel(e);
        }
    };

    render() {
        const styles = this.styles;

        return (
            <Modal
                headline={this.props.headlineText}
                {...styles.modal}
                isOpen={this.props.modalIsOpen}
                data-test-selector={this.props.dataTestSelector}
                handleClose={this.formCloseHandler}
            >
                <GridContainer {...styles.container}>
                    <GridItem {...styles.textGridItem}>
                        <StyledWrapper as="div" {...styles.messageText} data-test-selector="twoButtonModal_MessageContent">
                            {this.props.messageText}
                        </StyledWrapper>
                    </GridItem>
                    <GridItem {...styles.buttonsGridItem}>
                        <Button
                            {...styles.submitButton}
                            onClick={this.formSubmitHandler}
                            data-test-selector={this.props.submitTestSelector}
                        >
                            {this.props.submitButtonText}
                        </Button>
                        <Button {...styles.cancelButton} onClick={this.formCancelHandler}>
                            {this.props.cancelButtonText}
                        </Button>
                    </GridItem>
                </GridContainer>
            </Modal>
        );
    }
}

export default TwoButtonModal;
